import { useCallback, useEffect, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  MarkerType
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import axiosInstance, { handleApiError } from "../../../../services/axiosService";

import Loader from "../../../../Layouts/Loader/Loader";
import CustomJobNode from "./CustomJobNode";
import FlowSidebar from "./FlowSidebar";
import EdgeEditSidebar from "./EdgeEditSidebar";
import BuilderRightSideBar from "../../../../Layouts/BuilderRightSideBar";
import JobConfigration from "../../../../components/BuilderComponents/JobConfigration/JobConfigration";
import FormBuilderDeletePopup from "../../../../Layouts/Modal/FormBuilderDeletePopup";
import FormSubmittedNode from "./FormSubmittedNode";

import "./ActivityFlow.css";

const nodeTypes = {
  customJob: CustomJobNode,
  formSubmitted: FormSubmittedNode
};

const templateObject = {};

const ActivityFlow = ({ activityData, setActivityData, allJobs, smId, activityId }) => {
  const [smJobs, setSmJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectedJobNode, setSelectedJobNode] = useState(null);
  const [isJobEditOpen, setIsJobEditOpen] = useState(false);
  const [showJobDeletePopup, setShowJobDeletePopup] = useState(false);
  const { screenToFlowPosition } = useReactFlow();

  // Fetch jobs from API
  useEffect(() => {
    const fetchSmJobs = async () => {
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.get(`/jobs/filter?places=post_activity`);
        setSmJobs(data);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSmJobs();
  }, []);

  // Add Form Submitted node and create post activity job nodes
  useEffect(() => {
    const formSubmittedNode = {
      id: "form-submitted",
      type: "formSubmitted",
      position: { x: 50, y: 50 },
      data: {
        label: "Form Submitted",
        isFormSubmitted: true
      },
      draggable: true
    };

    const jobNodes = [];
    const jobEdges = [];

    // Create nodes from jobs_trigger (post activity jobs)
    if (activityData?.jobs_trigger && Array.isArray(activityData.jobs_trigger)) {
      activityData.jobs_trigger.forEach((job, index) => {
        const jobFromAllJobs = allJobs?.post_activity?.find((j) => j.id === job.job_id);

        if (jobFromAllJobs) {
          const nodeId = `job-${job.id || index}`;

          // Position nodes in a horizontal flow from left to right
          jobNodes.push({
            id: nodeId,
            type: "customJob",
            position: {
              x: 250 + index * 200,
              y: 50
            },
            data: {
              label: jobFromAllJobs.name,
              jobTemplate: jobFromAllJobs.job_template,
              jobId: jobFromAllJobs.id,
              category: jobFromAllJobs.category,
              isCustomNode: false,
              jobConfig: job
            }
          });

          // Connect to form submitted if it's the first job
          if (index === 0) {
            jobEdges.push({
              id: `edge-form-to-${nodeId}`,
              source: "form-submitted",
              target: nodeId,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 24,
                height: 24
              }
            });
          } else {
            // Connect to previous job
            const prevNodeId = `job-${activityData.jobs_trigger[index - 1].id || index - 1}`;
            jobEdges.push({
              id: `edge-${prevNodeId}-to-${nodeId}`,
              source: prevNodeId,
              target: nodeId,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 24,
                height: 24
              }
            });
          }
        }
      });
    }

    // Update nodes and edges
    setNodes((nds) => {
      const existingFormNode = nds.find((n) => n.id === "form-submitted");
      const existingJobNodeIds = new Set(nds.filter((n) => n.id.startsWith("job-")).map((n) => n.id));
      const newJobNodes = jobNodes.filter((jn) => !existingJobNodeIds.has(jn.id));

      if (!existingFormNode) {
        return [formSubmittedNode, ...nds, ...newJobNodes];
      }
      return [...nds, ...newJobNodes];
    });

    if (jobEdges.length > 0) {
      setEdges((eds) => {
        const existingEdgeIds = new Set(eds.map((e) => e.id));
        const edgesToAdd = jobEdges.filter((e) => !existingEdgeIds.has(e.id));
        return [...eds, ...edgesToAdd];
      });
    }
  }, [activityData?.jobs_trigger, allJobs, setNodes, setEdges]);

  // Handle connection between nodes with arrow
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const sourceNode = nodes.find((node) => node.id === params.source);

        // If it's a PA node (not a custom node), remove existing source connections
        if (sourceNode && !sourceNode.data.isCustomNode && !sourceNode.data.isFormSubmitted) {
          const filteredEdges = eds.filter((edge) => edge.source !== params.source);

          return addEdge(
            {
              ...params,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 24,
                height: 24
              }
            },
            filteredEdges
          );
        }

        // For custom nodes and form-submitted, just add the connection normally
        return addEdge(
          {
            ...params,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 24,
              height: 24
            }
          },
          eds
        );
      });
    },
    [setEdges, nodes]
  );

  // Handle edge click to open sidebar
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);
    setSelectedJobNode(null);
    setIsJobEditOpen(false);
  }, []);

  // Handle node click to open job edit sidebar (only for PA jobs, not custom nodes or form-submitted)
  const onNodeClick = useCallback((event, node) => {
    event.stopPropagation();
    if (!node.data.isCustomNode && !node.data.isFormSubmitted) {
      setSelectedJobNode(node);
      setIsJobEditOpen(true);
      setSelectedEdge(null);
    }
  }, []);

  // Save edge label changes
  const handleSaveEdge = useCallback(
    ({ label }) => {
      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id === selectedEdge.id) {
            return {
              ...edge,
              label
            };
          }
          return edge;
        })
      );
      setSelectedEdge(null);
    },
    [selectedEdge, setEdges]
  );

  // Delete edge
  const handleDeleteEdge = useCallback(() => {
    setEdges((eds) => eds.filter((edge) => edge.id !== selectedEdge.id));
    setSelectedEdge(null);
  }, [selectedEdge, setEdges]);

  // Close edge edit sidebar
  const handleCloseSidebar = useCallback(() => {
    // Deselect all edges before closing
    setEdges((eds) =>
      eds.map((e) => ({
        ...e,
        selected: false
      }))
    );
    setSelectedEdge(null);
  }, [setEdges]);

  // Close job edit sidebar
  const handleCloseJobEdit = useCallback(() => {
    // Deselect all nodes when closing sidebar
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: false
      }))
    );
    setIsJobEditOpen(false);
    setSelectedJobNode(null);
  }, [setNodes]);

  // Save job configuration
  const handleSaveJob = useCallback(
    (jobConfig) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === selectedJobNode.id) {
            return {
              ...node,
              data: {
                ...node.data,
                jobConfig: jobConfig,
                label: node.data.label
              }
            };
          }
          return node;
        })
      );

      const updatedJobsTrigger = activityData.jobs_trigger || [];
      const existingIndex = updatedJobsTrigger.findIndex((job) => job.id === jobConfig.id);

      let newJobsTrigger;
      if (existingIndex !== -1) {
        newJobsTrigger = updatedJobsTrigger.map((job, index) => (index === existingIndex ? jobConfig : job));
      } else {
        newJobsTrigger = [...updatedJobsTrigger, jobConfig];
      }

      setActivityData({
        ...activityData,
        jobs_trigger: newJobsTrigger
      });

      handleCloseJobEdit();
    },
    [selectedJobNode, activityData, setActivityData, setNodes, handleCloseJobEdit]
  );

  // Handle drop event for PA jobs
  const onDropJobNode = useCallback(
    (event) => {
      event.preventDefault();
      const jobData = event.dataTransfer.getData("application/reactflow");
      if (!jobData) return;

      const job = JSON.parse(jobData);
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });

      const newNode = {
        id: `${job.id}-${Date.now()}`,
        type: "customJob",
        position,
        data: {
          label: job.name,
          jobTemplate: job.job_template,
          jobId: job.id,
          category: job.category,
          isCustomNode: false
        }
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes]
  );

  // Handle drop event for custom nodes
  const onDropCustomNode = useCallback(
    (event) => {
      event.preventDefault();
      const customNodeData = event.dataTransfer.getData("application/customnode");
      if (!customNodeData) return;

      const customNode = JSON.parse(customNodeData);
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });

      const newNode = {
        id: `${customNode.id}-${Date.now()}`,
        type: "customJob",
        position,
        data: {
          label: customNode.name,
          jobTemplate: customNode.id,
          icon: customNode.icon,
          category: "Custom",
          isCustomNode: true
        }
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const handleJobDragStart = (event, job) => {
    event.dataTransfer.setData("application/reactflow", JSON.stringify(job));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleCustomNodeDragStart = (event, customNode) => {
    event.dataTransfer.setData("application/customnode", JSON.stringify(customNode));
    event.dataTransfer.effectAllowed = "move";
  };

  useEffect(() => {
    const handleDeleteResponse = (event) => {
      const response = event.detail;

      if (response === "delete" && showJobDeletePopup && selectedJobNode) {
        setNodes((nds) => nds.filter((node) => node.id !== selectedJobNode.id));
        setEdges((eds) =>
          eds.filter((edge) => edge.source !== selectedJobNode.id && edge.target !== selectedJobNode.id)
        );
        setActivityData((prev) => ({
          ...prev,
          jobs_trigger: (prev.jobs_trigger || []).filter((job) => job.job_id !== selectedJobNode.data.jobId)
        }));
        setIsJobEditOpen(false);
        setSelectedJobNode(null);
      }
      setShowJobDeletePopup(false);
    };
    document.addEventListener("userDeleteResponse", handleDeleteResponse);
    return () => {
      document.removeEventListener("userDeleteResponse", handleDeleteResponse);
    };
  }, [showJobDeletePopup, selectedJobNode, setNodes, setEdges, setActivityData, setIsJobEditOpen]);

  if (isLoading) {
    return <Loader />;
  }

  // Find the job configuration for the selected node
  const getSelectedJobConfig = () => {
    if (!selectedJobNode) return null;

    if (selectedJobNode.data.jobConfig) {
      return selectedJobNode.data.jobConfig;
    }

    // Return a default config based on the job from the node data
    return {
      job_id: selectedJobNode.data.jobId,
      job_template: selectedJobNode.data.jobTemplate,
      params: {},
      description: ""
    };
  };

  return (
    <div style={{ display: "flex", width: "100%", height: "calc(100vh - 136px)", position: "relative" }}>
      {/* Canvas Area */}
      <div
        className="activity_flow_main"
        onDrop={(e) => {
          onDropJobNode(e);
          onDropCustomNode(e);
        }}
        onDragOver={onDragOver}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          deleteKeyCode={null}
          nodesDeletable={false}
          edgesDeletable={false}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>

      {/* Job Edit Sidebar - Only for PA Job Nodes */}
      {isJobEditOpen && selectedJobNode && !selectedJobNode.data.isCustomNode && (
        <BuilderRightSideBar
          key={selectedJobNode.id}
          style={{ width: "600px" }}
          isOpen={isJobEditOpen}
          heading="Edit PA Job"
          onClose={handleCloseJobEdit}
          size="medium"
        >
          <div className="edit_activity_job">
            <JobConfigration
              editJob={getSelectedJobConfig()}
              smId={smId}
              actId={activityId}
              onClick={handleSaveJob}
              templateObject={templateObject}
              allJobs={allJobs?.post_activity || []}
              isSmJob={false}
              onDelete={() => setShowJobDeletePopup(true)}
            />
          </div>
        </BuilderRightSideBar>
      )}

      {showJobDeletePopup && <FormBuilderDeletePopup action="delete" message="this PA Job Node" info="confirmation" />}

      {/* Edge Edit Sidebar */}
      {selectedEdge && (
        <EdgeEditSidebar
          edge={selectedEdge}
          onSave={handleSaveEdge}
          onDelete={handleDeleteEdge}
          onClose={handleCloseSidebar}
        />
      )}

      {/* Flow Sidebar with Tabs - Only show when no edge or job is selected */}
      {!selectedEdge && !isJobEditOpen && (
        <FlowSidebar
          smJobs={smJobs}
          onJobDragStart={handleJobDragStart}
          onCustomNodeDragStart={handleCustomNodeDragStart}
        />
      )}
    </div>
  );
};

export default ActivityFlow;
